import { useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import type { NavigateFunction } from "react-router-dom";
import { appEvents } from "@/shared/lib/events";
import { useAgentChatStore } from "@/features/companion";
import type { SpeechResult } from "@/features/companion";
import {
  estimateOnboardingSpeechDelayMs,
  ONBOARDING_CURSOR_STEP_DELAY_MS,
} from "../../services/marcusCursorQueue";
import {
  EXPLORE_CARD_SPOTLIGHT_HOLD_MS,
  type OnboardingStage,
  type RunPhase,
} from "./marcusOnboardingFlow";
import { MARCUS_ONBOARDING_NEXT_TARGET as NEXT_TARGET } from "./MarcusOnboardingSpotlight";
import {
  COMPANION_AGENT_LIST_TEAM_SWEEP_TARGET,
  LAST_EXPLORE_TARGET,
  LONG_SPOTLIGHT_TARGETS,
  META_AGENT_TASKS_TARGET,
  ONBOARDING_TARGETS,
  STAGE_GATE_REVEAL_TARGETS,
  STATIC_ADCLONE_PRODUCT_AI_ADD_TARGET,
  STATIC_ADCLONE_PRODUCT_IMAGE_TARGET,
} from "./marcusOnboardingTargets";
import {
  handleStaticAdcloneCursorArrival,
  isStaticAdcloneProductAiAddReady,
  runStaticAdcloneAiAddStep,
  runStaticAdcloneDiscoveryStep,
} from "./marcusStaticAdcloneChoreo";

export interface MarcusOnboardingArrivalHandlerOptions {
  phase: RunPhase;
  activeStage: OnboardingStage | null;
  navigate: NavigateFunction;
  speakOnboardingHighlightAndWait: (
    comment: string | undefined,
    fallbackMs: number,
  ) => Promise<SpeechResult>;
  cursorStepRunRef: MutableRefObject<number>;
  clearSpotlightTimerRef: MutableRefObject<number | null>;
  nextGateTimerRef: MutableRefObject<number | null>;
  setPhase: Dispatch<SetStateAction<RunPhase>>;
  setSpotlightTarget: Dispatch<SetStateAction<string | null>>;
  setCompanionDockUnlocked: Dispatch<SetStateAction<boolean>>;
}

export function useMarcusOnboardingArrivalHandler(
  options: MarcusOnboardingArrivalHandlerOptions,
): void {
  const {
    phase,
    activeStage,
    navigate,
    speakOnboardingHighlightAndWait,
    cursorStepRunRef,
    clearSpotlightTimerRef,
    nextGateTimerRef,
    setPhase,
    setSpotlightTarget,
    setCompanionDockUnlocked,
  } = options;

  useEffect(() => {
    if (phase === "idle") return;
    const effectRunId = ++cursorStepRunRef.current;
    const isCurrentRun = () => cursorStepRunRef.current === effectRunId;
    let productImageStagedUnsub: (() => void) | null = null;
    let productCreatedUnsub: (() => void) | null = null;

    const completeCursorStep = (
      event: { stepId?: string; target: string },
      status: "completed" | "timeout" | "cancelled" | "skipped" = "completed",
    ) => {
      if (!event.stepId) return;
      appEvents.publish({
        type: "marcus-cursor-step-complete",
        stepId: event.stepId,
        target: event.target,
        status,
      });
    };

    const waitMs = (ms: number) =>
      new Promise<void>((resolve) => {
        const timer = window.setTimeout(() => {
          if (clearSpotlightTimerRef.current === timer) {
            clearSpotlightTimerRef.current = null;
          }
          resolve();
        }, Math.max(0, ms));
        clearSpotlightTimerRef.current = timer;
      });

    const waitForNarratedSpotlight = async (target: string, comment?: string) => {
      const minimumHoldMs = LONG_SPOTLIGHT_TARGETS.has(target)
        ? EXPLORE_CARD_SPOTLIGHT_HOLD_MS
        : 1600;
      const speechFallbackMs = comment
        ? estimateOnboardingSpeechDelayMs(comment)
        : ONBOARDING_CURSOR_STEP_DELAY_MS;
      const [, speechResult] = await Promise.all([
        waitMs(minimumHoldMs),
        speakOnboardingHighlightAndWait(comment, speechFallbackMs),
      ]);
      return speechResult;
    };

    const revealNextGate = () => {
      if (clearSpotlightTimerRef.current) {
        window.clearTimeout(clearSpotlightTimerRef.current);
        clearSpotlightTimerRef.current = null;
      }
      if (nextGateTimerRef.current) {
        window.clearTimeout(nextGateTimerRef.current);
        nextGateTimerRef.current = null;
      }
      setPhase("waiting_for_next");
      setSpotlightTarget(NEXT_TARGET);
    };

    const choreoCtx = {
      isCurrentRun,
      navigate,
      revealNextGate,
      setSpotlightTarget,
      speakOnboardingHighlightAndWait,
    };

    const unsubscribeArrived = appEvents.subscribe("marcus-cursor-arrived", (event) => {
      if (event.source && event.source !== "onboarding") return;
      if (!ONBOARDING_TARGETS.has(event.target)) return;
      void (async () => {
        if (event.target === NEXT_TARGET) {
          revealNextGate();
          completeCursorStep(event);
          return;
        }

        setSpotlightTarget(event.target);
        if (event.target === META_AGENT_TASKS_TARGET) {
          setCompanionDockUnlocked(true);
          const chat = useAgentChatStore.getState();
          chat.setMarcusOnboardingDockLocked(false);
          chat.requestDockAutoOpen("onboarding-showcase");
        }
        if (event.target === COMPANION_AGENT_LIST_TEAM_SWEEP_TARGET) {
          appEvents.publish({
            type: "marcus-onboarding:agent-list-sweep",
            runId: effectRunId,
          });
        }

        const speechResult = await waitForNarratedSpotlight(event.target, event.comment);
        if (!isCurrentRun()) {
          completeCursorStep(event, "cancelled");
          return;
        }

        // Hold the upload-image spotlight until the user actually uploads;
        // other targets clear spotlight as soon as narration finishes.
        const holdSpotlight =
          activeStage === "static_adclone" &&
          event.target === STATIC_ADCLONE_PRODUCT_IMAGE_TARGET;

        if (event.target === LAST_EXPLORE_TARGET || STAGE_GATE_REVEAL_TARGETS.has(event.target)) {
          revealNextGate();
        } else if (!holdSpotlight) {
          setSpotlightTarget((current) => (current === event.target ? null : current));
        }

        completeCursorStep(
          event,
          speechResult.status === "timeout"
            ? "timeout"
            : speechResult.status === "cancelled"
              ? "cancelled"
              : "completed",
        );

        if (activeStage === "static_adclone") {
          if (event.target === STATIC_ADCLONE_PRODUCT_IMAGE_TARGET) {
            const queueAiAddStep = () => {
              productImageStagedUnsub?.();
              productImageStagedUnsub = null;
              if (!isCurrentRun()) return;
              void runStaticAdcloneAiAddStep(choreoCtx);
            };
            productImageStagedUnsub?.();
            productImageStagedUnsub = appEvents.subscribe(
              "marcus-onboarding:product-image-staged",
              queueAiAddStep,
            );
            if (isStaticAdcloneProductAiAddReady()) queueAiAddStep();
          }
          if (event.target === STATIC_ADCLONE_PRODUCT_AI_ADD_TARGET) {
            productCreatedUnsub?.();
            productCreatedUnsub = appEvents.subscribe("product-created", () => {
              productCreatedUnsub?.();
              productCreatedUnsub = null;
              if (!isCurrentRun()) return;
              void runStaticAdcloneDiscoveryStep(choreoCtx);
            });
          }
          handleStaticAdcloneCursorArrival(event.target, choreoCtx);
        }
      })();
    });

    return () => {
      unsubscribeArrived();
      productImageStagedUnsub?.();
      productImageStagedUnsub = null;
      productCreatedUnsub?.();
      productCreatedUnsub = null;
      cursorStepRunRef.current += 1;
      if (clearSpotlightTimerRef.current) {
        window.clearTimeout(clearSpotlightTimerRef.current);
        clearSpotlightTimerRef.current = null;
      }
      if (nextGateTimerRef.current) {
        window.clearTimeout(nextGateTimerRef.current);
        nextGateTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStage, navigate, phase, speakOnboardingHighlightAndWait]);
}
